import { useFeature } from '@features/FeatureToggle';
import type { TpCardPlanChildrenProps } from '@features/Paywall/widgets/subscription-card/types';
import { useMemo } from 'react';
import { currencyReplacer } from 'src/utils/payment/currency-replacer';

type UseCurrentPriceProps = {
  plan: TpCardPlanChildrenProps['plan'];
  onlyFullPrice?: boolean;
};

export const useCurrentPrice = ({
  plan,
  onlyFullPrice,
}: UseCurrentPriceProps) => {
  const isIntroOfferEnabled = useFeature('introOffer');
  const fractionalPriceDiscount = useMemo(() => {
    if (isIntroOfferEnabled) {
      return (
        plan?.introOfferData?.fractionalPriceDiscount ??
        plan.fractionalPriceDiscount
      );
    } else {
      return plan.fractionalPriceDiscount;
    }
  }, [
    isIntroOfferEnabled,
    plan?.introOfferData?.fractionalPriceDiscount,
    plan.fractionalPriceDiscount,
  ]);

  const fullPriceDiscount = useMemo(() => {
    if (isIntroOfferEnabled) {
      return plan?.introOfferData?.fullPriceDiscount ?? plan.fullPriceDiscount;
    } else {
      return plan.fullPriceDiscount;
    }
  }, [
    isIntroOfferEnabled,
    plan?.introOfferData?.fullPriceDiscount,
    plan.fullPriceDiscount,
  ]);

  const initialPrice =
    plan.initialPrice !== undefined
      ? plan.initialPrice / (plan?.trialPeriodDays ?? 1)
      : null;

  const formattedPrice = useMemo(() => {
    if (initialPrice !== null) {
      return initialPrice.toFixed(2).toString().slice(2);
    } else if (fractionalPriceDiscount?.toString().split('.')[1] != null) {
      return Number.parseFloat(
        fractionalPriceDiscount
          .toString()
          .split('.')[1]
          .toString()
          .padEnd(2, '0'),
      ).toString();
    } else {
      return '00';
    }
  }, [initialPrice, fractionalPriceDiscount]);

  const displayPrice = plan.paddlePlanId
    ? plan.displayPrice
    : `per ${plan.displayPrice}`;

  const currency = currencyReplacer(plan.currency);

  const priceDigit =
    initialPrice !== null
      ? Number(formattedPrice[0])
      : Number(
          Number.parseFloat(
            fractionalPriceDiscount?.toString().split('.')[0] ?? '0',
          ),
        );

  const isLongCurrencies = priceDigit.toString().length > 2;

  return useMemo(() => {
    if (!plan) {
      return {
        displayPrice: null,
        formattedPrice: '00',
        currency: '',
        priceDigit: 0,
        isLongCurrencies: false,
      };
    }
    if (onlyFullPrice) {
      return {
        displayPrice: '',
        formattedPrice: Number(fullPriceDiscount).toString().split('.')[1],
        currency: currency,
        priceDigit: Number(fullPriceDiscount).toString().split('.')[0],
        isLongCurrencies,
      };
    }
    return {
      displayPrice,
      formattedPrice,
      currency,
      priceDigit,
      isLongCurrencies,
    };
  }, [
    plan,
    displayPrice,
    fullPriceDiscount,
    onlyFullPrice,
    formattedPrice,
    currency,
    isLongCurrencies,
    priceDigit,
  ]);
};
